import { LitElement, css, html, type PropertyValues } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { when } from 'lit/directives/when.js';
import { customElement, property, query } from 'lit/decorators.js';
import { question } from '../svg';

@customElement('umb-market-tooltip')
export class UmbMarketTooltip extends LitElement {
  @property({ type: String })
  text?: string;

  @property({ type: Boolean })
  in = false;

  @property({ type: Boolean })
  showtrigger = false;

  @property({ type: String })
  x: 'left' | 'right' = 'left';

  @property({ type: Boolean })
  wide = false;

  @query('small')
  private _tooltip?: HTMLElement;

  #gapX?: number;
  #gapY?: number;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.#show);
    this.addEventListener('mouseleave', this.#hide);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.#show);
    this.removeEventListener('mouseleave', this.#hide);
    super.disconnectedCallback();
  }

  firstUpdated() {
    const style = getComputedStyle(this);
    this.#gapY = parseFloat(style.getPropertyValue('--xs-spacer')) || 4;
    this.#gapX = parseFloat(style.getPropertyValue('--s-spacer')) || 8;
  }

  updated(changed: PropertyValues<this>) {
    if (!changed.has('in')) return;
    if (this.in) this.#show();
    else this.#hide();
  }

  // popover="manual" promotes the tooltip to the browser's top layer, so it
  // always renders above other content regardless of shadow-DOM stacking contexts
  #show = () => {
    const tooltip = this._tooltip;
    if (!tooltip || tooltip.matches(':popover-open')) return;

    const rect = this.getBoundingClientRect();
    const width = this.wide ? 320 : 200;

    tooltip.style.top = `${rect.bottom + this.#gapY!}px`;
    tooltip.style.left =
      this.x === 'right'
        ? `${rect.right - width - this.#gapX!}px`
        : `${rect.left + this.#gapX!}px`;

    tooltip.showPopover();
  };

  #hide = () => {
    if (this._tooltip?.matches(':popover-open')) this._tooltip.hidePopover();
  };

  render() {
    return html` <slot></slot>${when(this.showtrigger, () => html`${question}`)}
      <small popover="manual">${unsafeHTML(this.text)}</small>`;
  }

  static styles = [
    css`
      :host {
        display: flex;
        align-items: center;
      }

      small {
        position: fixed;
        margin: 0;
        border: none;
        width: 200px;
        border-radius: 4px;
        background: var(--uui-color-default);
        box-shadow: var(--box-shadow-blue);
        color: var(--uui-color-surface);
        padding: 4px 8px;
        line-height: 1.4;
        text-align: left;
        font-size: 13px;
        font-weight: 400;
      }

      :host([wide]) small {
        width: 320px;
      }

      ul {
        margin: 0;
        padding-left: 1rem;
      }

      svg {
        width: 18px;
        height: 18px;
      }
    `,
  ];
}
